import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import {
	useOfferDetail,
	useSendOffer,
	useDeleteOffer,
	useConvertToOrder,
	useCancelOffer,
} from "../hooks/use-offers";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { useWarehouseList, type Warehouse } from "../hooks/use-warehouses";
import { useStockItemDetail } from "../hooks/use-stock-items";
import { ShippingAddressPicker } from "../components/ui/ShippingAddressPicker/ShippingAddressPicker";
import { useToast } from "../lib/use-toast";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { formatDate, formatAmount } from "../lib/date";
import { computeOfferTotals, effectiveUnitPrice } from "@crm/shared/pricing";
import styles from "./OfferDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/offers/$id",
	component: withPermission(OfferDetailPage, "offers", "read"),
});

function OfferDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const offerQuery = useOfferDetail(id);
	const sendOffer = useSendOffer();
	const deleteOffer = useDeleteOffer();
	const convertToOrder = useConvertToOrder();
	const navigate = useNavigate();
	const cancelOffer = useCancelOffer();
	const { showToast } = useToast();
	const [sendOpen, setSendOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [convertOpen, setConvertOpen] = useState(false);
	const [cancelOpen, setCancelOpen] = useState(false);
	const [duplicateOpen, setDuplicateOpen] = useState(false);
	const [selectedAlternatives, setSelectedAlternatives] = useState<number[]>([]);
	const [convertDetails, setConvertDetails] = useState({
		deliveryDate: "",
		paymentTerms: "",
		shippingAddressId: "",
		shippingAddress: "",
	});
	const [lineWarehouses, setLineWarehouses] = useState<Record<string, string>>({});
	const warehouseList = useWarehouseList({ limit: 200 });

	if (offerQuery.isLoading) return <LoadingSpinner />;
	if (offerQuery.isError || !offerQuery.data) return <ErrorFallback onRetry={offerQuery.refetch} />;

	const offer = offerQuery.data;

	const alternativeGroups = [
		...new Map(
			(offer.lines ?? []).map((l) => [
				l.alternativeGroup ?? 0,
				{
					group: l.alternativeGroup ?? 0,
					currency: l.alternativeCurrency ?? offer.currency,
				},
			]),
		).values(),
	].sort((a, b) => a.group - b.group);
	const groupTotal = (group: number) =>
		(offer.lines ?? [])
			.filter((l) => (l.alternativeGroup ?? 0) === group)
			.reduce((sum, l) => sum + Number(l.quantity) * effectiveUnitPrice(l), 0);
	const totals = (offer.lines ?? []).length
		? computeOfferTotals(offer.lines ?? [])
		: {
				subtotal: Number(offer.subtotal) || 0,
				vatTotal: Number(offer.vatTotal) || 0,
				total: Number(offer.total) || 0,
			};
	const groupTitle = (group: number) => {
		const first = (offer.lines ?? []).find((l) => (l.alternativeGroup ?? 0) === group);
		return first?.displayDescription || first?.itemDescription || `#${group + 1}`;
	};
	const isMultiAlternative = alternativeGroups.length > 1;
	// Sipariş tek para biriminde açılıyor; ilk seçim para birimini sabitliyor.
	const selectedCurrency = alternativeGroups.find((g) =>
		selectedAlternatives.includes(g.group),
	)?.currency;
	const activeWarehouses = (warehouseList.data?.items ?? []).filter((w) => w.isActive);
	// "Faturada Gör" panoları ayrı seçilmez; siparişte bağlı oldukları jeneratörün deposunu alır.
	const convertLines = (offer.lines ?? []).filter(
		(l) =>
			!!l.stockItemId &&
			!l.isSection &&
			!(l.priceComponents ?? []).some((c) => c.separate) &&
			selectedAlternatives.includes(l.alternativeGroup ?? 0),
	);

	const hasAnyTerms =
		offer.termsCommissioning ||
		offer.termsAssembly ||
		offer.termsVat ||
		offer.termsQuality ||
		offer.termsValidity ||
		offer.termsWarranty ||
		offer.termsPayment ||
		offer.termsDelivery ||
		offer.termsPaymentMethod;

	const hasAnySapFields =
		offer.startupDescription ||
		offer.installationDescription ||
		offer.tmsDescription ||
		offer.atsDescription ||
		offer.factorySpecialNotes ||
		offer.generalNotes;

	return (
		<div>
			<BackButton fallbackTo="/offers" />
			<PageHeader
				title={offer.documentNumber || `${t("offer")} #${offer.id.slice(0, 8)}`}
				action={
					<div className={styles.actions}>
						<Link
							to="/contracts/new"
							search={{
								companyId: offer.companyId,
								orderId: "",
								offerId: offer.id,
							}}
						>
							<Button variant="secondary">{t("createContract")}</Button>
						</Link>
						{offer.isLatestVersion &&
							offer.status !== "converted_to_order" &&
							offer.status !== "cancelled" && (
								<Link to="/offers/$id/edit" params={{ id: offer.id }}>
									<Button>{t("edit")}</Button>
								</Link>
							)}
						{offer.status === "draft" && (
							<Button onClick={() => setSendOpen(true)} title={t("sendConvertToOrderTooltip")}>
								{t("sendConvertToOrder")}
							</Button>
						)}
						{(offer.status === "sent" || offer.status === "accepted") &&
							(offer.syncStatus === "synced" || isMultiAlternative) && (
								<Button
									onClick={() => {
										setSelectedAlternatives(
											isMultiAlternative ? [] : [alternativeGroups[0]?.group ?? 0],
										);
										setConvertDetails({
											deliveryDate: "",
											paymentTerms: offer.paymentTerms ?? "",
											shippingAddressId: "",
											shippingAddress: "",
										});
										setLineWarehouses({});
										setConvertOpen(true);
									}}
								>
									{t("convertToOrder")}
								</Button>
							)}
						{!!offer.sapDocEntry && (
							<Button
								variant="secondary"
								onClick={() => setDuplicateOpen(true)}
								title={t("duplicateOfferTooltip")}
							>
								{t("duplicateOffer")}
							</Button>
						)}
						{offer.status !== "cancelled" && offer.status !== "converted_to_order" && (
							<Button variant="secondary" onClick={() => setCancelOpen(true)}>
								{t("cancelDocument")}
							</Button>
						)}
						<Button variant="danger" onClick={() => setDeleteOpen(true)}>
							{t("delete")}
						</Button>
					</div>
				}
			/>

			{offer.leadId && !offer.companyId && !offer.sapDocEntry && (
				<div
					style={{
						color: "var(--color-danger)",
						fontSize: 13,
						marginBottom: 12,
					}}
				>
					{t("leadOfferNotSentToSap")}
				</div>
			)}

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info
							label={t("company")}
							value={
								offer.companyId ? (
									<Link
										to={`/companies/${offer.companyId}` as never}
										search={
											(offer.companySapAccountId
												? { account: offer.companySapAccountId }
												: undefined) as never
										}
										className={styles.link}
									>
										{offer.companyName ?? "-"}
										{offer.companySapAccountCurrency
											? ` (${offer.companySapAccountCurrency})`
											: offer.currency
												? ` (${offer.currency})`
												: ""}
									</Link>
								) : (
									"-"
								)
							}
						/>
						<Info
							label={t("validUntil")}
							value={offer.validUntil ? formatDate(offer.validUntil) : "-"}
						/>
						<Info label={t("currency")} value={offer.currency} />
						<Info label={t("paymentTerms")} value={offer.paymentTerms} />
						<Info label={t("deliveryTerms")} value={offer.deliveryTerms} />
						<Info
							label={t("offerDate")}
							value={offer.offerDate ? formatDate(offer.offerDate) : "-"}
						/>
						<Info label={t("sentAt")} value={offer.sentAt ? formatDate(offer.sentAt) : "-"} />
						<Info
							label={t("commitmentDuration")}
							value={
								offer.offerOrderDeliveryType === "stock"
									? t("stockDelivery")
									: offer.commitmentValue != null && offer.commitmentUnit
										? `${offer.commitmentValue} ${t(
												offer.commitmentUnit === "day"
													? "unitDay"
													: offer.commitmentUnit === "week"
														? "unitWeek"
														: "unitMonth",
											)}`
										: offer.commitmentDate
											? formatDate(offer.commitmentDate)
											: "-"
							}
						/>
						<Info label={t("isTaxIncluded")} value={offer.isTaxIncluded ? t("yes") : t("no")} />
						<Info label={t("showGtipInPdf")} value={offer.showGtipInPdf ? t("yes") : t("no")} />
						<Info label={t("factoryNotes")} value={offer.notes ?? "-"} />
						<Info label={t("pdfNotes")} value={offer.pdfNotes ?? "-"} />
						<Info label={t("buyerExtraInfo")} value={offer.buyerExtraInfo ?? "-"} />
						<Info label={t("contractNo")} value={offer.contractNo ?? "-"} />
						<Info
							label={t("contractDate")}
							value={offer.contractDate ? formatDate(offer.contractDate) : "-"}
						/>
						<Info label={t("grossTotal")} value={formatAmount(offer.grossTotal)} />
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge
								variant={
									offer.status === "sent"
										? "primary"
										: offer.status === "accepted"
											? "success"
											: offer.status === "rejected"
												? "danger"
												: "default"
								}
							>
								{t(offer.status)}
							</Badge>
						</div>
					</div>
				</Card>

				{hasAnyTerms && (
					<Card header={t("terms")}>
						<div className={styles.infoGrid}>
							{offer.termsCommissioning && (
								<Info label={t("termsCommissioning")} value={offer.termsCommissioning} />
							)}
							{offer.termsAssembly && (
								<Info label={t("termsAssembly")} value={offer.termsAssembly} />
							)}
							{offer.termsVat && <Info label={t("termsVat")} value={offer.termsVat} />}
							{offer.termsQuality && <Info label={t("termsQuality")} value={offer.termsQuality} />}
							{offer.termsValidity && (
								<Info label={t("termsValidity")} value={offer.termsValidity} />
							)}
							{offer.termsWarranty && (
								<Info label={t("termsWarranty")} value={offer.termsWarranty} />
							)}
							{offer.termsPayment && <Info label={t("termsPayment")} value={offer.termsPayment} />}
							{offer.termsDelivery && (
								<Info label={t("termsDelivery")} value={offer.termsDelivery} />
							)}
							{offer.termsPaymentMethod && (
								<Info label={t("termsPaymentMethod")} value={offer.termsPaymentMethod} />
							)}
						</div>
					</Card>
				)}

				{hasAnySapFields && (
					<Card header={t("sapFields")}>
						<div className={styles.infoGrid}>
							{offer.startupDescription && (
								<Info label={t("startupDescription")} value={offer.startupDescription} />
							)}
							{offer.installationDescription && (
								<Info label={t("installationDescription")} value={offer.installationDescription} />
							)}
							{offer.tmsDescription && (
								<Info label={t("tmsDescription")} value={offer.tmsDescription} />
							)}
							{offer.atsDescription && (
								<Info label={t("atsDescription")} value={offer.atsDescription} />
							)}
							{offer.factorySpecialNotes && (
								<Info label={t("factorySpecialNotes")} value={offer.factorySpecialNotes} />
							)}
							{offer.generalNotes && <Info label={t("generalNotes")} value={offer.generalNotes} />}
						</div>
					</Card>
				)}

				<Card header={t("lineItems")}>
					{(offer.lines ?? []).length === 0 && <p className={styles.empty}>{t("noData")}</p>}
					<table className={styles.lineTable}>
						<thead>
							<tr>
								<th>{t("itemCode")}</th>
								<th>{t("itemDescription")}</th>
								<th>{t("displayDescription")}</th>
								<th>{t("quantity")}</th>
								<th>{t("listPrice")}</th>
								<th>{t("givenPrice")}</th>
								<th>{t("discountPercent")}</th>
								<th>{t("lineTotal")}</th>
								<th>{t("lineDetail")}</th>
								<th>{t("showInPdf")}</th>
								<th>{t("showPriceInPdf")}</th>
								<th>{t("showDiscountInPdf")}</th>
								<th>Datasheet</th>
								<th>{t("sendDatasheetInEmail")}</th>
								<th>{t("isCustomDesign")}</th>
							</tr>
						</thead>
						<tbody>
							{(offer.lines ?? []).map((line) => (
								<tr key={line.id}>
									<td>{line.itemCode || "-"}</td>
									<td>{line.itemDescription || "-"}</td>
									<td>
										{line.displayDescription || line.itemDescription || "-"}
										{line.isSection &&
											(line.sectionItems ?? []).map((it, i) => (
												<div key={i} className={styles.sectionItemText}>
													{it.quantity > 1 ? `${it.quantity} ADET ${it.name}` : it.name}
												</div>
											))}
									</td>
									<td>{line.quantity}</td>
									<td>{formatAmount(line.unitPrice)}</td>
									<td>
										{(() => {
											const list = Number(line.unitPrice) || 0;
											const disc = Number(line.discountPercent) || 0;
											const given =
												line.givenUnitPrice != null && line.givenUnitPrice !== ""
													? Number(line.givenUnitPrice)
													: Math.round(list * (1 - disc / 100) * 100) / 100;
											return formatAmount(given);
										})()}
									</td>
									<td>
										{(() => {
											const disc = Number(line.discountPercent) || 0;
											const variant = disc > 0 ? "danger" : disc < 0 ? "success" : "default";
											const label =
												disc > 0 ? t("belowList") : disc < 0 ? t("aboveList") : t("atList");
											return (
												<Badge variant={variant}>
													%{formatAmount(line.discountPercent) || "0"} · {label}
												</Badge>
											);
										})()}
									</td>
									<td>{formatAmount(line.lineTotal)}</td>
									<td>{line.lineDetail || "-"}</td>
									<td>{line.showInPdf ? t("yes") : t("no")}</td>
									<td>{line.showPriceInPdf ? t("yes") : t("no")}</td>
									<td>{line.showDiscountInPdf ? t("yes") : t("no")}</td>
									<td>
										{line.itemDescription && (
											<Button
												size="sm"
												variant="ghost"
												onClick={async () => {
													try {
														const res = await fetch(
															`/api/nextcloud/datasheet?desc=${encodeURIComponent(line.itemDescription!)}`,
															{ credentials: "include" },
														);
														const data = (await res.json()) as { path?: string; error?: string };
														if (!res.ok) {
															alert(data.error ?? t("datasheetNotFound"));
															return;
														}
														window.open(
															`/api/nextcloud/file?path=${encodeURIComponent(data.path!)}`,
															"_blank",
														);
													} catch {
														alert(t("datasheetNotFound"));
													}
												}}
											>
												{t("view")}
											</Button>
										)}
									</td>
									<td>{line.sendDatasheetInEmail ? t("yes") : t("no")}</td>
									<td>{line.isCustomDesign ? t("yes") : t("no")}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Card>

				<Card>
					<div className={styles.totals}>
						<div className={styles.totalRow}>
							<span>{t("subtotal")}</span>
							<span>
								{formatAmount(totals.subtotal)} {offer.currency}
							</span>
						</div>
						<div className={styles.totalRow}>
							<span>{t("vatTotal")}</span>
							<span>
								{formatAmount(totals.vatTotal)} {offer.currency}
							</span>
						</div>
						<div className={[styles.totalRow, styles.grandTotal].join(" ")}>
							<span>{t("total")}</span>
							<span>
								{formatAmount(totals.total)} {offer.currency}
							</span>
						</div>
					</div>
				</Card>
			</div>

			<ConfirmDialog
				isOpen={sendOpen}
				onClose={() => setSendOpen(false)}
				onConfirm={() =>
					sendOffer.mutate(id, {
						onSuccess: () => {
							setSendOpen(false);
							alert(t("sendSuccess"));
						},
					})
				}
				message={t("confirmSend")}
				isLoading={sendOffer.isPending}
			/>

			<ConfirmDialog
				isOpen={convertOpen}
				onClose={() => setConvertOpen(false)}
				confirmDisabled={
					(isMultiAlternative && selectedAlternatives.length === 0) ||
					!convertDetails.deliveryDate ||
					!convertDetails.paymentTerms.trim()
				}
				onConfirm={() =>
					convertToOrder.mutate(
						{
							id,
							alternativeGroups: isMultiAlternative ? selectedAlternatives : undefined,
							deliveryDate: convertDetails.deliveryDate,
							paymentTerms: convertDetails.paymentTerms.trim(),
							shippingAddressId: convertDetails.shippingAddressId || undefined,
							shippingAddress: convertDetails.shippingAddress || undefined,
							lineWarehouses: Object.fromEntries(
								convertLines
									.filter((l) => lineWarehouses[l.id] !== undefined)
									.map((l) => [l.id, lineWarehouses[l.id] || null]),
							),
						},
						{
							onSuccess: (data) => {
								setConvertOpen(false);
								// Tam sayfa yeniden yükleme (window.location) yerine SPA yönlendirmesi:
								// uygulama yeniden başlatılmadığı için sipariş ekranı anında açılıyor.
								showToast(t("convertToOrder") + " " + t("createSuccess").toLowerCase(), "success");
								void navigate({ to: "/orders/$id", params: { id: data.id } });
							},
							onError: (err) => {
								setConvertOpen(false);
								showToast(err instanceof Error ? err.message : t("error"), "error");
							},
						},
					)
				}
				message={isMultiAlternative ? t("selectAlternativeToConvert") : t("confirmConvertToOrder")}
				isLoading={convertToOrder.isPending}
			>
				{isMultiAlternative && (
					<div className={styles.alternativeChoices}>
						{alternativeGroups.map(({ group, currency }, idx) => (
							<label
								key={group}
								className={styles.alternativeChoice}
								title={
									selectedCurrency && currency !== selectedCurrency
										? t("alternativeCurrencyMismatch")
										: undefined
								}
							>
								<input
									type="checkbox"
									name="convert-alternative"
									disabled={!!selectedCurrency && currency !== selectedCurrency}
									checked={selectedAlternatives.includes(group)}
									onChange={() =>
										setSelectedAlternatives((prev) =>
											prev.includes(group)
												? prev.filter((g) => g !== group)
												: [...prev, group].sort((a, b) => a - b),
										)
									}
								/>
								<span>
									<strong>
										{t("alternative")} {idx + 1}
									</strong>{" "}
									— {groupTitle(group)} · {formatAmount(groupTotal(group))} {currency}
								</span>
							</label>
						))}
					</div>
				)}
				<div className={styles.convertDetails}>
					<FormField label={t("deliveryDate")} required>
						<Input
							type="date"
							value={convertDetails.deliveryDate}
							onChange={(e) =>
								setConvertDetails((prev) => ({ ...prev, deliveryDate: e.target.value }))
							}
						/>
					</FormField>
					<FormField label={t("paymentTerms")} required>
						<Input
							value={convertDetails.paymentTerms}
							onChange={(e) =>
								setConvertDetails((prev) => ({ ...prev, paymentTerms: e.target.value }))
							}
						/>
					</FormField>
					{offer.companyId && (
						<FormField label={t("shippingAddress")}>
							<ShippingAddressPicker
								companyId={offer.companyId}
								value={convertDetails.shippingAddressId}
								onChange={(addressId, formatted) =>
									setConvertDetails((prev) => ({
										...prev,
										shippingAddressId: addressId,
										shippingAddress: formatted,
									}))
								}
							/>
						</FormField>
					)}
					{convertLines.length > 0 && (
						<div className={styles.lineWarehouses}>
							<div className={styles.lineWarehousesHeader}>
								<span>
									{t("lineWarehouses")} ({convertLines.length})
								</span>
								{convertLines.length > 1 && (
									<Select
										className={styles.lineWarehouseSelect}
										options={[
											{ value: "", label: t("applyWarehouseToAll") },
											...activeWarehouses.map((w) => ({
												value: w.id,
												label: `${w.code} - ${w.name}`,
											})),
										]}
										value=""
										onChange={(e) => {
											const warehouseId = e.target.value;
											if (!warehouseId) return;
											setLineWarehouses((prev) => ({
												...prev,
												...Object.fromEntries(convertLines.map((l) => [l.id, warehouseId])),
											}));
										}}
									/>
								)}
							</div>
							<div className={styles.lineWarehouseList}>
								{convertLines.map((line) => (
									<LineWarehouseSelect
										key={line.id}
										line={line}
										warehouses={activeWarehouses}
										value={lineWarehouses[line.id]}
										onChange={(warehouseId) =>
											setLineWarehouses((prev) => ({ ...prev, [line.id]: warehouseId }))
										}
									/>
								))}
							</div>
						</div>
					)}
				</div>
				{/* Sipariş teklifin sahibiyle açılır; burada sadece kontrol için gösterilir. */}
				<div className={styles.onBehalfRow}>
					<span>{t("onBehalfOf")}</span>
					<Input value={offer.assignedToName ?? offer.assignedToEmail ?? "-"} disabled readOnly />
					<small className={styles.onBehalfHint}>{t("convertOwnerHint")}</small>
				</div>
			</ConfirmDialog>

			<ConfirmDialog
				isOpen={cancelOpen}
				onClose={() => setCancelOpen(false)}
				onConfirm={() =>
					cancelOffer.mutate(id, {
						onSuccess: () => {
							setCancelOpen(false);
							showToast(t("cancelDocumentSuccess"), "success");
						},
						onError: (err) => {
							setCancelOpen(false);
							showToast(err instanceof Error ? err.message : t("error"), "error");
						},
					})
				}
				message={t("confirmCancelOffer")}
				isLoading={cancelOffer.isPending}
			/>

			<ConfirmDialog
				isOpen={duplicateOpen}
				onClose={() => setDuplicateOpen(false)}
				onConfirm={() => {
					setDuplicateOpen(false);
					showToast(t("duplicateOfferSuccess"), "success");
					navigate({ to: "/offers/new", search: { copyFrom: id } as never });
				}}
				message={t("confirmDuplicateOffer")}
			/>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteOffer.mutate(id, {
						onSuccess: () => {
							setDeleteOpen(false);
							alert(t("deleteSuccess"));
						},
						onError: (err) => {
							setDeleteOpen(false);
							showToast(err instanceof Error ? err.message : t("error"), "error");
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteOffer.isPending}
			/>
		</div>
	);
}

function LineWarehouseSelect({
	line,
	warehouses,
	value,
	onChange,
}: {
	line: {
		stockItemId: string | null;
		itemCode: string | null;
		itemDescription: string | null;
		displayDescription: string | null;
	};
	warehouses: Warehouse[];
	value: string | undefined;
	onChange: (warehouseId: string) => void;
}) {
	const { t } = useTranslation();
	const item = useStockItemDetail(line.stockItemId ?? "");
	const stocks = item.data?.warehouseStocks ?? [];

	// Eski sipariş formundaki gibi: önce kalemin varsayılan deposu, yoksa en çok stoklu depo.
	useEffect(() => {
		if (value !== undefined || !item.data || warehouses.length === 0) return;
		const byDefault = item.data.defaultWarehouseCode
			? warehouses.find((w) => w.code === item.data.defaultWarehouseCode)?.id
			: undefined;
		const byStock = [...(item.data.warehouseStocks ?? [])].sort(
			(a, b) => Number(b.currentStock) - Number(a.currentStock),
		)[0]?.warehouseId;
		onChange(byDefault ?? byStock ?? "");
	}, [value, item.data, warehouses, onChange]);

	const description = line.displayDescription || line.itemDescription || "";

	return (
		<div className={styles.lineWarehouseRow}>
			<div className={styles.lineWarehouseItem} title={description}>
				<strong>{line.itemCode}</strong>
				<span>{description}</span>
			</div>
			<Select
				className={styles.lineWarehouseSelect}
				aria-label={`${t("warehouse")} ${line.itemCode ?? ""}`}
				options={[
					{ value: "", label: "—" },
					...warehouses.map((w) => {
						const stock = stocks.find((s) => s.warehouseId === w.id)?.currentStock;
						const label = `${w.code} - ${w.name}`;
						return {
							value: w.id,
							label: stock != null ? `${label} (${Number(stock)})` : label,
						};
					}),
				]}
				value={value ?? ""}
				disabled={item.isLoading}
				onChange={(e) => onChange(e.target.value)}
			/>
		</div>
	);
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
